import { API_ENDPOINTS } from '@/config/config';

interface PasswordResetRequest {
  email: string;
}

interface PasswordResetConfirm {
  token: string;
  new_password: string;
}

interface PasswordResetResponse {
  message: string;
}

interface ApiError {
  message: string;
  status?: number;
  details?: Record<string, string[]>;
}

class PasswordResetServiceError extends Error {
  status?: number;
  details?: Record<string, string[]>;

  constructor(message: string, status?: number, details?: Record<string, string[]>) {
    super(message);
    this.name = 'PasswordResetServiceError';
    this.status = status;
    this.details = details;
  }
}

/**
 * Service for handling password reset operations
 */
export class PasswordResetService {
  private static async makeRequest<T>(url: string, options: RequestInit): Promise<T> {
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Handle different error status codes
        if (response.status === 429) {
          throw new PasswordResetServiceError(
            'Too many requests. Please wait before trying again.',
            response.status
          );
        }

        if (response.status === 400) {
          const message = errorData.email?.[0] || errorData.error || 'Invalid request';
          throw new PasswordResetServiceError(message, response.status, errorData);
        }

        if (response.status >= 500) {
          throw new PasswordResetServiceError(
            'Server error. Please try again later.',
            response.status
          );
        }

        const message = errorData.error || errorData.detail || 'Failed to process request';
        throw new PasswordResetServiceError(message, response.status, errorData);
      }

      return response.json();
    } catch (error) {
      if (error instanceof PasswordResetServiceError) {
        throw error;
      }

      // Network or other errors
      throw new PasswordResetServiceError(
        'Network error. Please check your connection and try again.'
      );
    }
  }

  /**
   * Request a password reset email
   */
  static async requestPasswordReset(email: string): Promise<PasswordResetResponse> {
    const payload: PasswordResetRequest = { email };

    return this.makeRequest<PasswordResetResponse>(API_ENDPOINTS.auth.passwordReset, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Confirm password reset with token and new password
   */
  static async confirmPasswordReset(
    token: string,
    newPassword: string
  ): Promise<PasswordResetResponse> {
    const payload: PasswordResetConfirm = {
      token,
      new_password: newPassword,
    };

    return this.makeRequest<PasswordResetResponse>(API_ENDPOINTS.auth.passwordResetConfirm, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

// Export the error class for use in components
export { PasswordResetServiceError };

// Export types
export type { PasswordResetRequest, PasswordResetConfirm, PasswordResetResponse, ApiError };
